/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

 // JavaScript to make the button sticky for mobile devices (max-width: 767px)
document.addEventListener('DOMContentLoaded', function() {
    const button_block = document.querySelector('.product-column-right .buy-buttons-row .quantity-submit-row__submit') ? document.querySelector('.product-column-right .buy-buttons-row .quantity-submit-row__submit') : '';
    
    if (button_block){
       const button = button_block.querySelector('.quantity-submit-row__submit-block');
       const button_height = button.getBoundingClientRect().height;
      
      // Check if the viewport width is less than or equal to 767 pixels
      if (window.innerWidth <= 767) {
          
          const interactElement = button_block.querySelector('.interact-js');
          // Function to toggle the 'sticky' class based on scroll position
          function toggleStickyClass() {

              const buttonPosition = button_block.getBoundingClientRect();
            
              if (buttonPosition.top >= window.innerHeight - button_height || buttonPosition.bottom + button_height <= 0) {
                  button.classList.add('sticky');
                  button.classList.remove('non-sticky');
                  interactElement.style.height = button_height + 'px'; // Set height to button height when sticky
                  document.querySelector('#shopify-section-footer').style.paddingBottom = button_height + 'px';
              } else {
                  button.classList.remove('sticky');
                  button.classList.add('non-sticky');
                  interactElement.style.height = '0'; // Set the height to 0 when non-sticky
                  document.querySelector('#shopify-section-footer').style.paddingBottom = '0';
              }
          }
  
          // Initial call to set the class based on the initial position
          toggleStickyClass();
  
          // Add a scroll event listener to toggle the class on scroll
          window.addEventListener('scroll', toggleStickyClass);
  
          // Add a resize event listener to reevaluate when the window is resized
          window.addEventListener('resize', toggleStickyClass);
      } else {
        button.classList.remove('sticky');
        button.classList.add('non-sticky');
        interactElement.style.height = '0'; // Set the height to 0 when non-sticky
        document.querySelector('#shopify-section-footer').style.paddingBottom = '0';
      }
      
  }
    
});

// Discount Badge on Selectors
function applyDiscountBadges(root = document) {
  const listedSelectors = root.querySelectorAll('.option-selector[data-selector-type="listed"]');
  if (!listedSelectors.length) return;

  const discountScripts = root.querySelectorAll('.product-label-list script[type="text/template"]');
  if (!discountScripts.length) return;

  const discounts = Array.from(discountScripts).map((script) => {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = script.innerHTML;
    const discountBadge = tempContainer.querySelector('.product-label--sale span');
    return discountBadge ? discountBadge.innerText : '';
  });

  let appliedDiscount = false;
  let discountIndex = 0;

  listedSelectors.forEach((selectorContainer) => {
    const variantInputs = selectorContainer.querySelectorAll('input.js-option');
    const selectorLabel = selectorContainer.querySelector('.label');
    const selectorIncludesBundles = selectorLabel && selectorLabel.innerText.toLowerCase().includes('bundles');

    variantInputs.forEach((input) => {
      const label = input.nextElementSibling;
      const badgeEl = label ? label.querySelector('.discount-badge') : null;
      if (!label || !badgeEl) return;

      const isSwatch = label.hasAttribute('data-swatch');
      const labelIncludesPack = label.innerText.toLowerCase().includes('pack');

      if (!isSwatch && (labelIncludesPack || selectorIncludesBundles)) {
        const discountText = discounts[discountIndex] || '';
        badgeEl.textContent = discountText;
        if (discountText) {
          appliedDiscount = true;
        }
        discountIndex++;
      } else {
        badgeEl.textContent = '';
      }
    });
  });

  const productLabelList = root.querySelector('.product-label-list');
  if (productLabelList) {
    productLabelList.style.display = appliedDiscount ? 'none' : 'block';
  }

  const banner = root.querySelector('.product-label-list .product-label--sale span');
  if (banner) {
    const checkedInput = root.querySelector('.option-selector[data-selector-type="listed"] input.js-option:checked');
    if (checkedInput) {
      const label = checkedInput.nextElementSibling;
      const discountText = label && label.querySelector('.discount-badge') ? label.querySelector('.discount-badge').textContent : '';
      banner.textContent = discountText;
    } else {
      banner.textContent = '';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  applyDiscountBadges();
});

document.addEventListener('on:quickbuy:after-open', (event) => {
  const quickbuyContainer = event.target.closest('.quickbuy-container') || event.target;
  const context = quickbuyContainer.querySelector('.quickbuy-content') || quickbuyContainer;
  applyDiscountBadges(context);
});


document.addEventListener('DOMContentLoaded', () => {
  // Check if main product section exists
  const mainProductSection = document.querySelector('.section-main-product');
  if (!mainProductSection) return;

  const cartButtons = mainProductSection.querySelectorAll('.shopify-payment-button__button--unbranded');
  const productForm = mainProductSection.querySelector('form[action="/cart/add"]');
  if (!cartButtons.length || !productForm) return;

  // Store base text for each button
  cartButtons.forEach(btn => {
    btn.dataset.baseText = btn.getAttribute('data-add-to-cart-text') || btn.textContent;
  });

  // Function to update button text with price
  const updateCartPrice = (variant) => {
    if (!variant) return;
    const price = (variant.price / 100).toFixed(2);
    cartButtons.forEach(btn => {
      btn.textContent = `${btn.dataset.baseText} – $${price}`;
    });
  };

  // Set initial price
  const currentVariantId = productForm.querySelector('[name=id]').value;
  if (currentVariantId) {
    fetch(`${window.location.pathname}.js`)
      .then(res => res.json())
      .then(product => {
        const variant = product.variants.find(v => v.id == currentVariantId);
        updateCartPrice(variant);
      });
  }

  // Listen for variant changes emitted by theme
  document.addEventListener('variant:changed', (event) => {
    updateCartPrice(event.detail.variant);
  });

  // Fallback: form change
  productForm.addEventListener('change', () => {
    const variantId = productForm.querySelector('[name=id]').value;
    if (!variantId) return;
    fetch(`${window.location.pathname}.js`)
      .then(res => res.json())
      .then(product => {
        const variant = product.variants.find(v => v.id == variantId);
        updateCartPrice(variant);
      });
  });
});


